/* eslint-disable react-refresh/only-export-components */
/**
 * Lazy Route Loading Configuration
 *
 * Centralized lazy loading for route components to improve initial bundle size
 * and application startup time.
 */

import React, { Suspense, useCallback, useEffect } from 'react';
import { TableSkeleton } from '@/components/ui/loading-skeleton';
import { lazyWithRetry } from './lazy-with-retry';

/**
 * Loading fallback components
 */
export function PageLoadingFallback() {
  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="space-y-2">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="h-4 w-32 bg-muted animate-pulse rounded" />
      </div>
      <TableSkeleton rows={8} columns={5} />
    </div>
  );
}

export function SimpleLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center space-y-2">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Helper to wrap lazy components with Suspense
 */
export function withSuspense(
  LazyComponent: React.LazyExoticComponent<any>,
  fallback: React.ReactNode = <PageLoadingFallback />
) {
  return (props: any) => (
    <Suspense fallback={fallback}>
      <LazyComponent {...props} />
    </Suspense>
  );
}

// =============================================================================
// REPORTS MODULE
// =============================================================================

export const LazyReports = lazyWithRetry(() => import('@/pages/Reports'));

// =============================================================================
// FINANCE MODULE
// =============================================================================

export const LazyFinanceQueue = lazyWithRetry(() => import('@/pages/FinanceQueue'));
export const LazyFinanceDashboard = lazyWithRetry(() => import('@/pages/finance/FinanceDashboard'));
export const LazyContributions = lazyWithRetry(() => import('@/pages/Contributions'));

// =============================================================================
// COMMUNITY MODULE
// =============================================================================

export const LazyCommunity = lazyWithRetry(() => import('@/pages/Community'));

// =============================================================================
// OPERATIONS MODULE
// =============================================================================

export const LazyProjects = lazyWithRetry(() => import('@/pages/Projects'));
export const LazyRequests = lazyWithRetry(() => import('@/pages/Requests'));
export const LazyAssets = lazyWithRetry(() => import('@/pages/Assets'));

// =============================================================================
// ATTENDANCE & EVENTS
// =============================================================================

export const LazyAttendance = lazyWithRetry(() => import('@/pages/Attendance'));
export const LazyCalendar = lazyWithRetry(() => import('@/pages/Calendar'));
export const LazyEventDetail = lazyWithRetry(() => import('@/pages/EventDetail'));

// =============================================================================
// ADMIN & SETTINGS
// =============================================================================

export const LazyAdminDashboard = lazyWithRetry(() => import('@/pages/AdminDashboard'));
export const LazyIntegrations = lazyWithRetry(() => import('@/pages/Integrations'));

// =============================================================================
// MESSAGES & COMMUNICATION
// =============================================================================

export const LazyMessages = lazyWithRetry(() => import('@/pages/Messaging'));

// =============================================================================
// HR MODULE
// =============================================================================

export const LazyHrDashboard = lazyWithRetry(() => import('@/pages/HrDashboard'));
export const LazyHrStaff = lazyWithRetry(() => import('@/pages/HrStaff'));
export const LazyHrPositions = lazyWithRetry(() => import('@/pages/HrPositions'));
export const LazyHrRecruitment = lazyWithRetry(() => import('@/pages/HrRecruitment'));
export const LazyHrOnboarding = lazyWithRetry(() => import('@/pages/HrOnboarding'));
export const LazyHrOffboarding = lazyWithRetry(() => import('@/pages/HrOffboarding'));
export const LazyHrTimeOff = lazyWithRetry(() => import('@/pages/HrTimeOff'));
export const LazyHrReviews = lazyWithRetry(() => import('@/pages/HrReviews'));
export const LazyHrDocuments = lazyWithRetry(() => import('@/pages/HrDocuments'));
export const LazyHrRequests = lazyWithRetry(() => import('@/pages/HrRequests'));
export const LazyHrSettings = lazyWithRetry(() => import('@/pages/HrSettings'));
export const LazyHrStaffProfile = lazyWithRetry(() => import('@/pages/HrStaffProfile'));

/**
 * Route preloading utilities - simplified since React.lazy doesn't support preload
 */
export function preloadRoute(route: React.LazyExoticComponent<any>) {
  const maybePrefetch = route as React.LazyExoticComponent<any> & {
    preload?: () => Promise<void>;
  };
  if (typeof maybePrefetch.preload === 'function') {
    maybePrefetch.preload().catch(() => {
      // Ignore prefetch errors to avoid blocking navigation.
    });
  }
}

export function preloadRoutes(routes: React.LazyExoticComponent<any>[]) {
  routes.forEach(preloadRoute);
}

/**
 * Common route groups for batch preloading
 */
export const ROUTE_GROUPS = {
  finance: [LazyFinanceDashboard],
  community: [LazyCommunity],
  reports: [LazyReports],
  operations: [LazyProjects],
  admin: [],
  events: [LazyCalendar],
  hr: [LazyHrDashboard],
};

const ROUTE_ENTRY_PREFETCH_GROUPS = {
  "/finance": [LazyFinanceDashboard, LazyFinanceQueue],
  "/people": [LazyCommunity],
  "/people/all": [LazyCommunity],
  "/contributions": [LazyContributions],
} as const;

export function isModuleEntryRoute(path: string): path is keyof typeof ROUTE_ENTRY_PREFETCH_GROUPS {
  return path in ROUTE_ENTRY_PREFETCH_GROUPS;
}

export function prefetchModuleEntryRoutes(path: string) {
  if (!isModuleEntryRoute(path)) {
    return;
  }

  preloadRoutes(ROUTE_ENTRY_PREFETCH_GROUPS[path]);
}

/**
 * Preload related routes based on current location
 */
export function preloadRelatedRoutes(currentPath: string) {
  const rootSegment = currentPath.split("/").filter(Boolean)[0];
  if (!rootSegment) return;

  if (rootSegment === "finance") {
    preloadRoutes(ROUTE_GROUPS.finance);
    return;
  }
  if (rootSegment === "community") {
    preloadRoutes(ROUTE_GROUPS.community);
    return;
  }
  if (rootSegment === "reports") {
    preloadRoutes(ROUTE_GROUPS.reports);
    return;
  }
  if (rootSegment === "projects" || rootSegment === "requests" || rootSegment === "operations") {
    preloadRoutes(ROUTE_GROUPS.operations);
    return;
  }
  if (rootSegment === "settings" || rootSegment === "admin") {
    preloadRoutes(ROUTE_GROUPS.admin);
    return;
  }
  if (rootSegment === "attendance" || rootSegment === "calendar") {
    preloadRoutes(ROUTE_GROUPS.events);
    return;
  }
  if (rootSegment === "hr") {
    preloadRoutes(ROUTE_GROUPS.hr);
  }
}

/**
 * Hook to preload routes on mount
 */
export function usePreloadRoutes(routes: React.LazyExoticComponent<any>[]) {
  useEffect(() => {
    const timer = setTimeout(() => {
      preloadRoutes(routes);
    }, 2000);

    return () => clearTimeout(timer);
  }, [routes]);
}

/**
 * Hook to preload routes on hover
 */
export function usePreloadOnHover(route: React.LazyExoticComponent<any>) {
  const handleMouseEnter = useCallback(() => {
    preloadRoute(route);
  }, [route]);

  return { onMouseEnter: handleMouseEnter };
}